// src/components/layout/Sidebar.js
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import LanguageSwitcher from '../LanguageSwitcher';
import './Sidebar.css';
import { 
  RiDashboardLine,
  RiBuildingLine, 
  RiTeamLine, 
  RiUserAddLine, 
  RiCalendarLine,
  RiCalendarCheckLine,
  RiHeartPulseLine,
  RiFileChartLine, 
  RiSettings4Line, 
  RiLogoutBoxLine
} from 'react-icons/ri';

const Sidebar = ({ isCollapsed, toggleSidebar, isMobile, remainingTime }) => {
  const { logout, currentCompany, currentUser } = useAuth();
  const { t } = useTranslation('common');

  const handleNavClick = () => {
    if (isMobile) {
      toggleSidebar();
    }
  };

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <>      
      <div className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <span className="sidebar-logo-badge">K</span>
            {!isCollapsed && <h3>Klokify</h3>}
          </div>
          {!isMobile && (
            <button onClick={toggleSidebar} className="toggle-btn">
              {isCollapsed ? '→' : '←'}
            </button>
          )}
        </div>

        <nav className="sidebar-nav">

          {/* ÜBERSICHT */}
          <div className="nav-group">
            {!isCollapsed && (
              <span className="nav-group-title">{t('navigation.groups.overview')}</span>
            )}
            <ul className="sidebar-menu">
              <li>
                <NavLink to="/admin/dashboard" className={({ isActive }) => isActive ? 'active' : ''} onClick={handleNavClick}>
                  <span className="icon"><RiDashboardLine /></span>
                  {!isCollapsed && <span className="label">{t('navigation.dashboard')}</span>}
                </NavLink>
              </li>
            </ul>
          </div>

          {/* TEAM */}
          <div className="nav-group">
            {!isCollapsed && (
              <span className="nav-group-title">{t('navigation.groups.team')}</span>
            )}
            <ul className="sidebar-menu">
              <li>
                <NavLink to="/admin/departments" className={({ isActive }) => isActive ? 'active' : ''} onClick={handleNavClick}>
                  <span className="icon"><RiBuildingLine /></span>
                  {!isCollapsed && <span className="label">{t('navigation.departments')}</span>}
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/staff" className={({ isActive }) => isActive ? 'active' : ''} onClick={handleNavClick}>
                  <span className="icon"><RiTeamLine /></span>
                  {!isCollapsed && <span className="label">{t('navigation.staff')}</span>}
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/staff/new" className={({ isActive }) => isActive ? 'active' : ''} onClick={handleNavClick}>
                  <span className="icon"><RiUserAddLine /></span>
                  {!isCollapsed && <span className="label">{t('buttons.add')} {t('navigation.staff')}</span>}
                </NavLink>
              </li>
            </ul>
          </div>

          {/* PLANUNG */}
          <div className="nav-group">
            {!isCollapsed && (
              <span className="nav-group-title">{t('navigation.groups.planning')}</span>
            )}
            <ul className="sidebar-menu">
              <li>
                <NavLink to="/admin/schedule-planner" className={({ isActive }) => isActive ? 'active' : ''} onClick={handleNavClick}>
                  <span className="icon"><RiCalendarLine /></span>
                  {!isCollapsed && <span className="label">{t('navigation.schedulePlanner')}</span>}
                </NavLink>
              </li>

              {/* Team Vacation - Available to all authenticated web users */}
              <li>
                <NavLink to="/admin/team-vacation" className={({ isActive }) => isActive ? 'active' : ''} onClick={handleNavClick}>
                  <span className="icon"><RiCalendarCheckLine /></span>
                  {!isCollapsed && <span className="label">{t('navigation.teamVacation')}</span>}
                </NavLink>
              </li>

              {/* Sick Leave - Available to all authenticated web users */}
              <li>
                <NavLink to="/admin/sick-leave" className={({ isActive }) => isActive ? 'active' : ''} onClick={handleNavClick}>
                  <span className="icon"><RiHeartPulseLine /></span>
                  {!isCollapsed && <span className="label">{t('navigation.sickLeave')}</span>}
                </NavLink>
              </li>
            </ul>
          </div>

          {/* AUSWERTUNG */}
          <div className="nav-group">
            {!isCollapsed && (
              <span className="nav-group-title">{t('navigation.groups.analytics')}</span>
            )}
            <ul className="sidebar-menu">
              <li>
                <NavLink to="/admin/reports" className={({ isActive }) => isActive ? 'active' : ''} onClick={handleNavClick}>
                  <span className="icon"><RiFileChartLine /></span>
                  {!isCollapsed && <span className="label">{t('navigation.reports')}</span>}
                </NavLink>
              </li>
              <li>
                <NavLink to="/admin/settings" className={({ isActive }) => isActive ? 'active' : ''} onClick={handleNavClick}>
                  <span className="icon"><RiSettings4Line /></span>
                  {!isCollapsed && <span className="label">{t('navigation.settings')}</span>}
                </NavLink>
              </li>
            </ul>
          </div>

        </nav>
        
        <div className="sidebar-footer">
          <div className={`language-switcher-wrapper ${isCollapsed ? 'collapsed' : ''}`}>
            {!isCollapsed && (
              <span className="activity-timer">{formatTime(remainingTime)}</span>
            )}
            <LanguageSwitcher />
            {isCollapsed && (
              <span className="activity-timer-collapsed">{Math.floor(remainingTime / 60)}</span>
            )}
          </div>
          <button onClick={logout} className="logout-btn">
            <span className="icon"><RiLogoutBoxLine /></span>
            {!isCollapsed && <span className="label">{t('buttons.logout')}</span>}
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;